import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Button, Alert, Spinner, Badge, ProgressBar, ButtonGroup, Offcanvas, Form, ListGroup } from "react-bootstrap";
import { Document, Page, pdfjs } from 'react-pdf';
import { ReactReader } from 'react-reader';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import "../styles/Cookbooks.scss";
import {
  getCachedCookbook,
  cacheCookbook,
  formatBytes
} from '../services/cookbookCache';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// Constants for zoom and font sizes
const MIN_ZOOM = 50;
const MAX_ZOOM = 200;
const ZOOM_STEP = 10;
const DEFAULT_ZOOM = 100;

// Debounce delay for saving progress to server
const SAVE_DEBOUNCE_MS = 3000;

function CookbookReader() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [cookbook, setCookbook] = useState(null);
  const [accessUrl, setAccessUrl] = useState(null);
  const [epubUrl, setEpubUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [loadedFromCache, setLoadedFromCache] = useState(false);

  // Download progress state
  const [downloadProgress, setDownloadProgress] = useState({
    downloading: false,
    loaded: 0,
    total: 0,
    percentage: 0
  });

  // PDF-specific state
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageWidth, setPageWidth] = useState(800);

  // ePub-specific state
  const [epubLocation, setEpubLocation] = useState(null);

  // Fullscreen and zoom state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM);

  // Bookmarks state
  const [bookmarks, setBookmarks] = useState([]);
  const [showBookmarkPanel, setShowBookmarkPanel] = useState(false);
  const [newBookmarkNote, setNewBookmarkNote] = useState('');

  // Ref to track ongoing fetch and prevent duplicates
  const fetchInProgress = useRef(false);
  const abortControllerRef = useRef(null);
  const currentIdRef = useRef(id);
  const readerContainerRef = useRef(null);
  const saveProgressTimeoutRef = useRef(null);

  useEffect(() => {
    currentIdRef.current = id;
    fetchCookbookAccess();

    // Set up auto-refresh for presigned URL (every 55 minutes)
    // Only for non-epub files since epub is fully downloaded
    const refreshInterval = setInterval(() => {
      if (cookbook?.file_type !== 'epub' && !loadedFromCache) {
        console.log("Refreshing presigned URL...");
        fetchCookbookAccess();
      }
    }, 55 * 60 * 1000); // 55 minutes

    return () => {
      clearInterval(refreshInterval);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [id]);

  // Handle window resize for PDF
  useEffect(() => {
    const handleResize = () => {
      const width = Math.min(window.innerWidth * 0.9, 1200);
      setPageWidth(width);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cleanup blob URL when component unmounts
  useEffect(() => {
    return () => {
      if (epubUrl && !loadedFromCache) {
        URL.revokeObjectURL(epubUrl);
      }
    };
  }, [epubUrl, loadedFromCache]);

  // Load saved position and zoom from localStorage on mount
  useEffect(() => {
    if (id) {
      // Load saved page/location
      const savedPosition = localStorage.getItem(`cookbook_position_${id}`);
      if (savedPosition) {
        try {
          const parsed = JSON.parse(savedPosition);
          if (parsed.pageNumber) {
            setPageNumber(parsed.pageNumber);
          }
          if (parsed.epubLocation) {
            setEpubLocation(parsed.epubLocation);
          }
        } catch (e) {
          console.warn('Failed to parse saved position:', e);
        }
      }

      // Load saved zoom level for PDF
      const savedZoom = localStorage.getItem(`cookbook_zoom_${id}`);
      if (savedZoom) {
        const zoom = parseInt(savedZoom, 10);
        if (zoom >= MIN_ZOOM && zoom <= MAX_ZOOM) {
          setZoomLevel(zoom);
        }
      }

      // Load bookmarks from server
      fetchBookmarks();
    }
  }, [id]);

  // Fetch reading progress from server on mount
  useEffect(() => {
    if (id && cookbook) {
      fetchReadingProgress();
    }
  }, [id, cookbook]);

  // Save position to localStorage when it changes
  useEffect(() => {
    if (id && (pageNumber > 1 || epubLocation)) {
      const position = {
        pageNumber: pageNumber,
        epubLocation: epubLocation
      };
      localStorage.setItem(`cookbook_position_${id}`, JSON.stringify(position));

      // Debounced save to server
      if (saveProgressTimeoutRef.current) {
        clearTimeout(saveProgressTimeoutRef.current);
      }
      saveProgressTimeoutRef.current = setTimeout(() => {
        saveReadingProgress();
      }, SAVE_DEBOUNCE_MS);
    }
  }, [id, pageNumber, epubLocation]);

  // Save zoom level to localStorage when it changes
  useEffect(() => {
    if (id) {
      localStorage.setItem(`cookbook_zoom_${id}`, zoomLevel.toString());
    }
  }, [id, zoomLevel]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Cleanup save timeout on unmount
  useEffect(() => {
    return () => {
      if (saveProgressTimeoutRef.current) {
        clearTimeout(saveProgressTimeoutRef.current);
        // Save immediately on unmount if there's pending save
        saveReadingProgress();
      }
    };
  }, []);

  // Fetch reading progress from server
  const fetchReadingProgress = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks/${id}/progress`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.progress) {
          // Only restore if we don't already have a local position
          const localSaved = localStorage.getItem(`cookbook_position_${id}`);
          if (!localSaved) {
            if (data.progress.page_number) {
              setPageNumber(data.progress.page_number);
            }
            if (data.progress.epub_location) {
              setEpubLocation(data.progress.epub_location);
            }
          }
        }
      }
    } catch (err) {
      console.warn('Failed to fetch reading progress:', err);
    }
  };

  // Save reading progress to server
  const saveReadingProgress = async () => {
    if (!id || !cookbook) return;

    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks/${id}/progress`,
        {
          method: 'POST',
          credentials: "include",
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pageNumber: pageNumber,
            epubLocation: epubLocation,
            totalPages: numPages,
            fileType: cookbook.file_type
          })
        }
      );
    } catch (err) {
      console.warn('Failed to save reading progress:', err);
    }
  };

  // Fetch bookmarks from server
  const fetchBookmarks = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks/${id}/bookmarks`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setBookmarks(data.bookmarks || []);
      }
    } catch (err) {
      console.warn('Failed to fetch bookmarks:', err);
    }
  };

  // Add a bookmark
  const addBookmark = async () => {
    const bookmark = {
      pageNumber: cookbook?.file_type === 'pdf' ? pageNumber : null,
      epubLocation: cookbook?.file_type === 'epub' ? epubLocation : null,
      label: cookbook?.file_type === 'pdf' ? `Page ${pageNumber}` : 'Bookmark',
      note: newBookmarkNote
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks/${id}/bookmarks`,
        {
          method: 'POST',
          credentials: "include",
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(bookmark)
        }
      );
      if (response.ok) {
        const data = await response.json();
        setBookmarks(prev => [...prev, data.bookmark]);
        setNewBookmarkNote('');
      }
    } catch (err) {
      console.error('Failed to add bookmark:', err);
    }
  };

  // Delete a bookmark
  const deleteBookmark = async (bookmarkId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks/${id}/bookmarks/${bookmarkId}`,
        {
          method: 'DELETE',
          credentials: "include"
        }
      );
      if (response.ok) {
        setBookmarks(prev => prev.filter(b => b.id !== bookmarkId));
      }
    } catch (err) {
      console.error('Failed to delete bookmark:', err);
    }
  };

  // Navigate to bookmark
  const goToBookmark = (bookmark) => {
    if (bookmark.page_number) {
      setPageNumber(bookmark.page_number);
    } else if (bookmark.epub_location) {
      setEpubLocation(bookmark.epub_location);
    }
    setShowBookmarkPanel(false);
  };

  // Check if current position is bookmarked
  const isCurrentPositionBookmarked = () => {
    if (cookbook?.file_type === 'pdf') {
      return bookmarks.some(b => b.page_number === pageNumber);
    } else if (cookbook?.file_type === 'epub' && epubLocation) {
      return bookmarks.some(b => b.epub_location === epubLocation);
    }
    return false;
  };

  // Toggle fullscreen
  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await readerContainerRef.current?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Failed to toggle fullscreen:', err);
    }
  };

  // Zoom functions
  const zoomIn = () => {
    setZoomLevel(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  };

  const zoomOut = () => {
    setZoomLevel(prev => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  };

  const resetZoom = () => {
    setZoomLevel(DEFAULT_ZOOM);
  };

  // Calculate actual page width based on zoom level
  const getZoomedPageWidth = useCallback(() => {
    const baseWidth = Math.min(window.innerWidth * 0.9, 1200);
    return baseWidth * (zoomLevel / 100);
  }, [zoomLevel]);

  // Get ePub reader styles
  const getEpubReaderStyles = useCallback(() => {
    return {
      container: {
        height: '100%',
        width: '100%'
      },
      readerArea: {
        backgroundColor: '#fff'
      },
      reader: {
        maxWidth: '800px',
        margin: '0 auto'
      }
    };
  }, []);

  const fetchCookbookAccess = async () => {
    // Prevent duplicate fetches (e.g., from React StrictMode)
    if (fetchInProgress.current) {
      return;
    }

    fetchInProgress.current = true;
    abortControllerRef.current = new AbortController();
    const thisRequestId = id; // Capture current id for this request

    try {
      // First fetch metadata to determine storage type and file info
      const metaResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks/${id}`,
        { credentials: "include" }
      );

      if (!metaResponse.ok) {
        throw new Error("Failed to access cookbook");
      }

      const metaData = await metaResponse.json();
      setCookbook(metaData.cookbook);

      // Check if we have this cookbook cached
      const cached = await getCachedCookbook(id);
      if (cached) {
        setLoadedFromCache(true);

        if (metaData.cookbook.file_type === 'epub') {
          const blobUrl = URL.createObjectURL(cached.blob);
          setEpubUrl(blobUrl);
          setAccessUrl(blobUrl);
        } else {
          // For PDFs, create blob URL
          const blobUrl = URL.createObjectURL(cached.blob);
          setAccessUrl(blobUrl);
        }

        setLoading(false);
        return;
      }

      // Not cached - need to download
      // For Google Drive files, use the proxy endpoint to avoid CORS issues
      // For S3 files, use the presigned URL
      let fileUrl;
      if (metaData.cookbook.storage_type === 'google_drive') {
        fileUrl = `${process.env.REACT_APP_API_URL}/api/cookbooks/${id}/proxy`;
      } else {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/cookbooks/${id}/access-url`,
          { credentials: "include" }
        );

        if (!response.ok) {
          throw new Error("Failed to get access URL");
        }

        const data = await response.json();
        fileUrl = data.url;
      }

      // Download all files with progress and cache them
      // This ensures subsequent loads are instant and free (no S3/network charges)

      setDownloadProgress({
        downloading: true,
        loaded: 0,
        total: metaData.cookbook.file_size || 0,
        percentage: 0
      });

      const fileResponse = await fetch(fileUrl, {
        credentials: "include",
        signal: abortControllerRef.current.signal
      });

      if (!fileResponse.ok) {
        throw new Error('Failed to download file');
      }

      // Check if user navigated away while downloading
      if (thisRequestId !== currentIdRef.current) {
        // User navigated away
        return;
      }

      // Stream the response to track progress
      const reader = fileResponse.body.getReader();
      const contentLength = parseInt(fileResponse.headers.get('Content-Length')) || metaData.cookbook.file_size || 0;
      const chunks = [];
      let receivedLength = 0;

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        chunks.push(value);
        receivedLength += value.length;

        // Update progress
        const percentage = contentLength > 0 ? Math.round((receivedLength / contentLength) * 100) : 0;
        setDownloadProgress({
          downloading: true,
          loaded: receivedLength,
          total: contentLength,
          percentage
        });
      }

      // Check again if user navigated away
      if (thisRequestId !== currentIdRef.current) {
        // User navigated away
        return;
      }

      // Combine chunks into blob
      const blob = new Blob(chunks, {
        type: metaData.cookbook.file_type === 'epub' ? 'application/epub+zip' : 'application/pdf'
      });

      // Cache the file
      try {
        await cacheCookbook(id, blob, {
          title: metaData.cookbook.title,
          fileType: metaData.cookbook.file_type
        });
      } catch (cacheError) {
        console.warn('Failed to cache cookbook:', cacheError);
        // Continue anyway - caching failure shouldn't break viewing
      }

      // Create blob URL and set state
      const blobUrl = URL.createObjectURL(blob);

      if (metaData.cookbook.file_type === 'epub') {
        setEpubUrl(blobUrl);
      }
      setAccessUrl(blobUrl);

      setDownloadProgress({
        downloading: false,
        loaded: 0,
        total: 0,
        percentage: 0
      });

      setLoading(false);
    } catch (err) {
      if (err.name === 'AbortError') {
        return;
      }
      console.error('Error loading cookbook:', err);
      setError(err.message);
      setLoading(false);
    } finally {
      fetchInProgress.current = false;
    }
  };

  const cancelDownload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    navigate('/cookbooks');
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const goToPrevPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const goToNextPage = () => {
    if (pageNumber < numPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const goToPage = (page) => {
    const pageNum = parseInt(page);
    if (pageNum >= 1 && pageNum <= numPages) {
      setPageNumber(pageNum);
    }
  };

  // Download progress UI
  if (downloadProgress.downloading) {
    return (
      <Container className="text-center mt-5">
        <div className="download-progress-container">
          <h4 className="mb-4">Downloading Cookbook</h4>
          <p className="text-muted mb-3">{cookbook?.title}</p>

          <ProgressBar
            now={downloadProgress.percentage}
            label={`${downloadProgress.percentage}%`}
            animated
            striped
            className="mb-3"
            style={{ height: '25px' }}
          />

          <p className="download-stats">
            {formatBytes(downloadProgress.loaded)}
            {downloadProgress.total > 0 && ` / ${formatBytes(downloadProgress.total)}`}
          </p>

          <p className="text-muted small mt-3">
            This download will be cached for instant access next time
          </p>

          <Button
            variant="outline-secondary"
            onClick={cancelDownload}
            className="mt-3"
          >
            Cancel
          </Button>
        </div>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading cookbook...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Cookbook</Alert.Heading>
          <p>{error}</p>
          <hr />
          <div className="d-flex justify-content-end">
            <Button onClick={() => navigate('/cookbooks')} variant="outline-danger">
              Back to Library
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  if (!cookbook || !accessUrl) {
    return (
      <Container className="text-center mt-5">
        <Alert variant="warning">Cookbook not found</Alert>
        <Button onClick={() => navigate('/cookbooks')}>Back to Library</Button>
      </Container>
    );
  }

  // Calculate progress percentage
  const progressPercentage = numPages ? Math.round((pageNumber / numPages) * 100) : 0;

  return (
    <Container fluid className={`cookbook-reader ${isFullscreen ? 'fullscreen-mode' : ''}`} ref={readerContainerRef}>
      {/* Header */}
      <div className="reader-header">
        <Button
          variant="outline-secondary"
          onClick={() => navigate('/cookbooks')}
          size="sm"
        >
          <i className="bi bi-arrow-left me-2"></i>
          Back to Library
        </Button>
        <div className="d-flex align-items-center flex-grow-1 ms-3 me-3 reader-title-section">
          <h3 className="mb-0 text-truncate">{cookbook.title}</h3>
          {loadedFromCache && (
            <Badge bg="success" className="ms-2 d-none d-md-inline-flex">
              <i className="bi bi-hdd me-1"></i>
              Cached
            </Badge>
          )}
        </div>

        {/* Reader Controls */}
        <div className="reader-controls d-flex align-items-center gap-2">
          {/* Progress indicator for PDF */}
          {cookbook.file_type === 'pdf' && numPages && (
            <div className="progress-indicator d-none d-lg-flex align-items-center me-2">
              <small className="text-muted me-2">{progressPercentage}%</small>
              <ProgressBar
                now={progressPercentage}
                style={{ width: '80px', height: '6px' }}
                variant="success"
              />
            </div>
          )}

          {/* Zoom controls for PDF */}
          {cookbook.file_type === 'pdf' && (
            <ButtonGroup size="sm" className="d-none d-md-flex">
              <Button
                variant="outline-secondary"
                onClick={zoomOut}
                disabled={zoomLevel <= MIN_ZOOM}
                title="Zoom out"
              >
                −
              </Button>
              <Button
                variant="outline-secondary"
                onClick={resetZoom}
                title="Reset zoom"
                style={{ minWidth: '55px' }}
              >
                {zoomLevel}%
              </Button>
              <Button
                variant="outline-secondary"
                onClick={zoomIn}
                disabled={zoomLevel >= MAX_ZOOM}
                title="Zoom in"
              >
                +
              </Button>
            </ButtonGroup>
          )}

          {/* Bookmark button */}
          <Button
            variant={isCurrentPositionBookmarked() ? "warning" : "outline-secondary"}
            size="sm"
            onClick={() => setShowBookmarkPanel(true)}
            title="Bookmarks"
          >
            {isCurrentPositionBookmarked() ? '★' : '☆'}
            {bookmarks.length > 0 && (
              <Badge bg="secondary" className="ms-1">{bookmarks.length}</Badge>
            )}
          </Button>

          {/* Fullscreen toggle */}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? '⊙' : '⛶'}
          </Button>
        </div>
      </div>

      {/* PDF Viewer */}
      {cookbook.file_type === 'pdf' && (
        <div className="pdf-viewer">
          <Document
            file={accessUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) => {
              console.error("Error loading PDF:", error);
              setError("Failed to load PDF. Please try again.");
            }}
            loading={
              <div className="text-center p-5">
                <Spinner animation="border" />
                <p className="mt-2">Loading PDF...</p>
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              width={getZoomedPageWidth()}
              renderTextLayer={true}
              renderAnnotationLayer={true}
            />
          </Document>

          {/* PDF Controls */}
          {numPages && (
            <div className="pdf-controls">
              <Button
                variant="outline-primary"
                size="sm"
                disabled={pageNumber <= 1}
                onClick={goToPrevPage}
              >
                <i className="bi bi-chevron-left"></i> Previous
              </Button>

              <div className="page-info">
                <span className="me-2">Page</span>
                <input
                  type="number"
                  min="1"
                  max={numPages}
                  value={pageNumber}
                  onChange={(e) => goToPage(e.target.value)}
                  style={{ width: '60px' }}
                  className="form-control form-control-sm d-inline"
                />
                <span className="ms-2">of {numPages}</span>
              </div>

              <Button
                variant="outline-primary"
                size="sm"
                disabled={pageNumber >= numPages}
                onClick={goToNextPage}
              >
                Next <i className="bi bi-chevron-right"></i>
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ePub Viewer */}
      {cookbook?.file_type === 'epub' && epubUrl && (
        <div className="epub-viewer" style={{ height: isFullscreen ? '100vh' : '85vh' }}>
          <ReactReader
            url={epubUrl}
            location={epubLocation}
            locationChanged={(epubcfi) => setEpubLocation(epubcfi)}
            title={cookbook.title}
            showToc={true}
            epubInitOptions={{
              openAs: 'epub'
            }}
            styles={getEpubReaderStyles()}
          />
        </div>
      )}

      {/* ePub Loading State */}
      {cookbook?.file_type === 'epub' && !epubUrl && !error && (
        <div className="text-center mt-5">
          <Spinner animation="border" />
          <p className="mt-3">Loading ePub file...</p>
        </div>
      )}

      {/* Bookmark Panel */}
      <Offcanvas
        show={showBookmarkPanel}
        onHide={() => setShowBookmarkPanel(false)}
        placement="end"
        className="bookmark-panel"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            ☆ Bookmarks
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {/* Add new bookmark section */}
          <div className="add-bookmark-section mb-4">
            <h6 className="mb-2">Add Bookmark at Current Position</h6>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Add a note (optional)"
              value={newBookmarkNote}
              onChange={(e) => setNewBookmarkNote(e.target.value)}
              className="mb-2"
            />
            <Button
              variant="primary"
              size="sm"
              onClick={addBookmark}
              disabled={isCurrentPositionBookmarked()}
              className="w-100"
            >
              {isCurrentPositionBookmarked() ? 'Already Bookmarked' : '+ Add Bookmark'}
            </Button>
          </div>

          <hr />

          {/* Bookmarks list */}
          <h6 className="mb-3">Saved Bookmarks ({bookmarks.length})</h6>
          {bookmarks.length === 0 ? (
            <p className="text-muted text-center">No bookmarks yet</p>
          ) : (
            <ListGroup variant="flush">
              {bookmarks.map((bookmark) => (
                <ListGroup.Item
                  key={bookmark.id}
                  className="d-flex justify-content-between align-items-start py-3"
                >
                  <div
                    className="bookmark-item-content flex-grow-1 cursor-pointer"
                    onClick={() => goToBookmark(bookmark)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => e.key === 'Enter' && goToBookmark(bookmark)}
                  >
                    <div className="fw-semibold">
                      {bookmark.label || (bookmark.page_number ? `Page ${bookmark.page_number}` : 'Bookmark')}
                    </div>
                    {bookmark.note && (
                      <small className="text-muted d-block mt-1">{bookmark.note}</small>
                    )}
                    <small className="text-muted">
                      {new Date(bookmark.created_at).toLocaleDateString()}
                    </small>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteBookmark(bookmark.id);
                    }}
                    title="Delete bookmark"
                  >
                    ✕
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}

export default CookbookReader;
